import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement, Tooltip, Legend, Title } from "chart.js";
import { Line } from "react-chartjs-2";
import jsPDF from "jspdf";
import { Link } from "react-router-dom";
import { supabase } from "./supabaseClient";
import AuthModal from "./AuthModal";
import { extractSirsComponentsFromPdf } from "./tools/pdfExtractor";

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Tooltip, Legend, Title);

type TabKey = "upload" | "funding" | "owner";

type SirsComponent = {
  name: string;
  estimatedReplacementCost: number;
  remainingUsefulLifeYears: number;
  currentReserveBalance: number;
};

type FundingInputs = {
  totalCurrentReserveBalance: number;
  annualContributionAmount: number;
  inflationRatePct: number;
  interestRatePct: number;
};

type ProjectionYear = {
  year: number;
  startingBalance: number;
  contributions: number;
  interestEarned: number;
  replacementsDue: number;
  endingBalance: number;
};

type Scenario = {
  label: string;
  inputs: FundingInputs;
  minAnnualContribution: number;
  projection: ProjectionYear[];
};

const BRAND = {
  navy: "#1B3A5C",
  teal: "#2A9D8F",
  bg: "#FFFFFF",
  light: "#F3F7FB",
  border: "#D2DEE9",
};

const REQUIRED_COMPONENTS = [
  "Roof",
  "Load-bearing structure",
  "Fire protection",
  "Plumbing",
  "Electrical",
  "Waterproofing/exterior painting",
  "Windows/exterior doors",
  "Other items over $25,000",
];
const UPGRADE_URL = "https://buy.stripe.com/dRmaEXb2LgVR7BF61U6Na00";

const defaultComponents: SirsComponent[] = REQUIRED_COMPONENTS.map((name) => ({
  name,
  estimatedReplacementCost: 0,
  remainingUsefulLifeYears: 0,
  currentReserveBalance: 0,
}));

const defaultFundingInputs: FundingInputs = {
  totalCurrentReserveBalance: 0,
  annualContributionAmount: 0,
  inflationRatePct: 3,
  interestRatePct: 4,
};

function asCurrency(value: number): string {
  return value.toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 });
}

function asCurrencyPrecise(value: number): string {
  return value.toLocaleString("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function parseNumber(input: string): number {
  const normalized = input.replace(/[^\d.-]/g, "");
  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : 0;
}

function buildProjection(components: SirsComponent[], inputs: FundingInputs, annualContribution: number): ProjectionYear[] {
  const inflation = inputs.inflationRatePct / 100;
  const interest = inputs.interestRatePct / 100;
  const rows: ProjectionYear[] = [];
  let balance = inputs.totalCurrentReserveBalance;

  for (let year = 1; year <= 30; year += 1) {
    const startingBalance = balance;
    const contributions = annualContribution;
    const interestEarned = Math.max(0, startingBalance + contributions) * interest;
    let replacementsDue = 0;

    components.forEach((component) => {
      const rul = Math.max(1, Math.round(component.remainingUsefulLifeYears || 0));
      if (year % rul === 0) {
        replacementsDue += component.estimatedReplacementCost * Math.pow(1 + inflation, year);
      }
    });

    const endingBalance = startingBalance + contributions + interestEarned - replacementsDue;
    rows.push({
      year,
      startingBalance,
      contributions,
      interestEarned,
      replacementsDue,
      endingBalance,
    });
    balance = endingBalance;
  }

  return rows;
}

function findMinimumAnnualContribution(components: SirsComponent[], inputs: FundingInputs): { minimum: number; projection: ProjectionYear[] } {
  const initial = buildProjection(components, inputs, inputs.annualContributionAmount);
  if (initial.every((row) => row.endingBalance >= 0)) {
    return { minimum: inputs.annualContributionAmount, projection: initial };
  }

  let low = 0;
  let high = Math.max(1000, inputs.annualContributionAmount * 2 || 10000);

  while (buildProjection(components, inputs, high).some((row) => row.endingBalance < 0)) {
    high *= 2;
    if (high > 100000000) break;
  }

  for (let i = 0; i < 60; i += 1) {
    const mid = (low + high) / 2;
    const projection = buildProjection(components, inputs, mid);
    if (projection.some((row) => row.endingBalance < 0)) {
      low = mid;
    } else {
      high = mid;
    }
  }

  // Keep cent precision to avoid visible rounding artifacts in final-year balances.
  let minimum = Math.ceil(high * 100) / 100;

  // Tighten downward to the smallest passing cent value.
  while (minimum > 0) {
    const test = Math.max(0, Number((minimum - 0.01).toFixed(2)));
    const testProjection = buildProjection(components, inputs, test);
    if (testProjection.some((row) => row.endingBalance < 0)) break;
    minimum = test;
  }

  return { minimum, projection: buildProjection(components, inputs, minimum) };
}

function MainApp() {
  const [activeTab, setActiveTab] = useState<TabKey>("upload");
  const [session, setSession] = useState<any>(null);
  const [userPlan, setUserPlan] = useState<string>("free");
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [components, setComponents] = useState<SirsComponent[]>(defaultComponents);
  const [pdfName, setPdfName] = useState("");
  const [uploadError, setUploadError] = useState("");
  const [isParsing, setIsParsing] = useState(false);
  const [fundingInputs, setFundingInputs] = useState<FundingInputs>(defaultFundingInputs);
  const [savedScenario, setSavedScenario] = useState<Scenario | null>(null);
  const [associationName, setAssociationName] = useState("Sample Condominium Association");
  const [numberOfUnits, setNumberOfUnits] = useState(120);
  const ownerChartRef = useRef<any>(null);
  const [focusedMoney, setFocusedMoney] = useState<null | { index: number; field: "erc" | "crb" }>(null);
  const [cancelSubscriptionLoading, setCancelSubscriptionLoading] = useState(false);

  const fetchProfile = useCallback(async (userId: string) => {
    const { data } = await supabase.from("profiles").select("plan").eq("id", userId).single();
    if (data?.plan != null) setUserPlan(String(data.plan));
  }, []);

  const plan = userPlan.toLowerCase().replace(/-/g, "_");
  const isPro = plan === "pro" || plan === "audit_ready";
  const isProPlanOnly = plan === "pro";

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session: s } }: { data: { session: any } }) => {
      setSession(s);
      if (s?.user) void fetchProfile(s.user.id);
      else setUserPlan("free");
    });
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event: any, s: any) => {
      setSession(s);
      if (s?.user) void fetchProfile(s.user.id);
      else setUserPlan("free");
    });
    return () => subscription.unsubscribe();
  }, [fetchProfile]);

  const handleCancelSubscription = useCallback(async () => {
    const email = session?.user?.email;
    if (!email) return;
    const ok = window.confirm(
      "Are you sure you want to cancel? You'll lose access to Pro features at the end of your billing period."
    );
    if (!ok) return;
    setCancelSubscriptionLoading(true);
    try {
      const res = await fetch("/api/cancel-subscription", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });
      const data = (await res.json().catch(() => ({}))) as { error?: string };
      if (!res.ok) {
        window.alert(data.error || "Could not cancel subscription.");
        return;
      }
      setUserPlan("free");
    } catch {
      window.alert("Network error. Please try again.");
    } finally {
      setCancelSubscriptionLoading(false);
    }
  }, [session]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const shareId = params.get("share");
    if (!shareId) return;
    const payload = localStorage.getItem(`florida-sirs-share-${shareId}`);
    if (!payload) return;
    try {
      const parsed = JSON.parse(payload);
      if (parsed.components) setComponents(parsed.components);
      if (parsed.fundingInputs) setFundingInputs(parsed.fundingInputs);
      if (parsed.associationName) setAssociationName(parsed.associationName);
      if (parsed.numberOfUnits) setNumberOfUnits(parsed.numberOfUnits);
    } catch {
      setUploadError("Could not load shared scenario from this URL.");
    }
  }, []);

  const minimumContributionResult = useMemo(
    () => findMinimumAnnualContribution(components, fundingInputs),
    [components, fundingInputs]
  );
  const currentProjection = useMemo(
    () => buildProjection(components, fundingInputs, fundingInputs.annualContributionAmount),
    [components, fundingInputs]
  );
  const contributionsInsufficient = currentProjection.some((row) => row.endingBalance < 0);
  /** Full 30-year projection used everywhere internally; gate display with slice for free tier. */
  const projectionData = contributionsInsufficient ? minimumContributionResult.projection : currentProjection;
  const visibleProjection = isPro ? projectionData : projectionData.slice(0, 10);

  const monthlyRequiredPerUnit = useMemo(() => {
    const annualNeeded = contributionsInsufficient
      ? minimumContributionResult.minimum
      : fundingInputs.annualContributionAmount;
    return numberOfUnits > 0 ? annualNeeded / 12 / numberOfUnits : 0;
  }, [contributionsInsufficient, minimumContributionResult.minimum, fundingInputs.annualContributionAmount, numberOfUnits]);

  const minimumMonthlyPerUnit = useMemo(() => {
    return numberOfUnits > 0 ? minimumContributionResult.minimum / 12 / numberOfUnits : 0;
  }, [minimumContributionResult.minimum, numberOfUnits]);

  const topThreeUpcoming = useMemo(() => {
    const inflation = fundingInputs.inflationRatePct / 100;
    return [...components]
      .filter((c) => c.estimatedReplacementCost > 0 && c.remainingUsefulLifeYears > 0)
      .sort((a, b) => a.remainingUsefulLifeYears - b.remainingUsefulLifeYears)
      .slice(0, 3)
      .map((c) => ({
        name: c.name,
        years: c.remainingUsefulLifeYears,
        futureCost: c.estimatedReplacementCost * Math.pow(1 + inflation, c.remainingUsefulLifeYears),
      }));
  }, [components, fundingInputs.inflationRatePct]);

  async function handlePdfUpload(file: File) {
    setUploadError("");
    setIsParsing(true);
    setPdfName(file.name);
    try {
      const normalized = (await extractSirsComponentsFromPdf(file)) as SirsComponent[];
      setComponents(normalized);
      const reserveTotal = normalized.reduce((sum, row) => sum + row.currentReserveBalance, 0);
      setFundingInputs((prev) => ({ ...prev, totalCurrentReserveBalance: reserveTotal }));
    } catch (error: any) {
      console.error("Claude upload/parsing error object:", error);
      setUploadError(error?.message || "Could not parse the uploaded report.");
    } finally {
      setIsParsing(false);
    }
  }

  function updateComponent(index: number, field: keyof SirsComponent, value: string) {
    setComponents((prev) =>
      prev.map((row, i) => {
        if (i !== index) return row;
        if (field === "name") return { ...row, name: value };
        return { ...row, [field]: parseNumber(value) };
      })
    );
  }

  function moneyFieldDisplay(row: SirsComponent, index: number, field: "erc" | "crb"): string {
    const v = field === "erc" ? row.estimatedReplacementCost : row.currentReserveBalance;
    if (focusedMoney?.index === index && focusedMoney?.field === field) {
      return v === 0 ? "" : String(v);
    }
    return asCurrency(v);
  }

  function updateFundingField(field: keyof FundingInputs, value: string) {
    setFundingInputs((prev) => ({ ...prev, [field]: parseNumber(value) }));
  }

  function copyShareableLink() {
    const shareId = Date.now().toString(36);
    const payload = {
      components,
      fundingInputs,
      associationName,
      numberOfUnits,
    };
    localStorage.setItem(`florida-sirs-share-${shareId}`, JSON.stringify(payload));
    const url = `${window.location.origin}${window.location.pathname}?share=${shareId}`;
    navigator.clipboard.writeText(url);
    alert("Shareable link copied to clipboard.");
  }

  function downloadOwnerPdf() {
    const doc = new jsPDF("p", "pt", "letter");
    doc.setFontSize(16);
    doc.setTextColor(27, 58, 92);
    doc.text(`${associationName} — SIRS Reserve Funding Plan`, 40, 40);
    doc.setFontSize(11);
    doc.setTextColor(0, 0, 0);
    doc.text(`Association: ${associationName}`, 40, 65);
    doc.text(`Report date: ${new Date().toLocaleDateString()}`, 40, 82);
    doc.text(`Monthly assessment required per unit: ${asCurrency(monthlyRequiredPerUnit)}`, 40, 99);

    let y = 130;
    topThreeUpcoming.forEach((item, idx) => {
      doc.text(
        `${idx + 1}. ${item.name} - ${asCurrency(item.futureCost)} in ~${item.years} years`,
        40,
        y
      );
      y += 18;
    });

    const chartCanvas = ownerChartRef.current?.canvas;
    if (chartCanvas) {
      const image = chartCanvas.toDataURL("image/png");
      doc.addImage(image, "PNG", 40, 200, 520, 240);
    }

    doc.text(
      "This projection estimates reserve funding needs over 30 years using your expected inflation and interest assumptions.",
      40,
      465
    );
    doc.text("The minimum contribution level is calculated to keep reserves from dropping below zero in any year.", 40, 483);

    const disclaimer =
      "FloridaSIRS.org generates financial projections based on data from your Structural Integrity Reserve Study. " +
      "This report does not constitute legal, engineering, or financial advice. Consult your licensed professionals " +
      "for compliance determinations. FloridaSIRS.org is not affiliated with the Florida Department of Business and Professional Regulation.";
    const disclaimerLines = doc.splitTextToSize(disclaimer, 530);
    doc.setFontSize(9);
    doc.setTextColor(70, 70, 70);
    doc.text(disclaimerLines, 40, 520);

    doc.setFontSize(9);
    doc.setTextColor(90, 90, 90);
    doc.text(
      "Generated by FloridaSIRS.org | Florida HB 913 Compliant Baseline Funding Plan",
      40,
      752
    );
    doc.save("FloridaSIRS-Owner-Report.pdf");
  }

  function downloadSampleSirsPdf() {
    const doc = new jsPDF("p", "pt", "letter");
    const left = 50;

    const sampleRows = [
      ["Roof", "$680,000", "7", "$45,000"],
      ["Load-bearing structure", "$2,100,000", "22", "$180,000"],
      ["Fire protection", "$340,000", "12", "$28,000"],
      ["Plumbing", "$890,000", "15", "$67,000"],
      ["Electrical", "$560,000", "18", "$41,000"],
      ["Waterproofing/exterior painting", "$420,000", "4", "$89,000"],
      ["Windows/exterior doors", "$780,000", "11", "$52,000"],
      ["Elevators", "$290,000", "9", "$23,000"],
    ];

    // Cover page
    doc.setFillColor(27, 58, 92);
    doc.rect(0, 0, 612, 110, "F");
    doc.setTextColor(255, 255, 255);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(24);
    doc.text("Structural Integrity Reserve Study", left, 68);

    doc.setTextColor(27, 58, 92);
    doc.setFontSize(16);
    doc.text("Oceanview Towers Condominium Association", left, 165);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(12);
    doc.text("2847 Collins Avenue", left, 190);
    doc.text("Miami Beach, FL 33140", left, 208);
    doc.text("Total Units: 120", left, 226);
    doc.text("Original Construction Year: 1987", left, 244);
    doc.text(`Report Date: ${new Date().toLocaleDateString()}`, left, 262);

    doc.setDrawColor(42, 157, 143);
    doc.setLineWidth(2);
    doc.line(left, 282, 562, 282);
    doc.setFontSize(11);
    doc.setTextColor(40, 40, 40);
    doc.text(
      "Prepared for annual reserve planning in accordance with Florida SIRS requirements.",
      left,
      306
    );
    doc.text(
      "This sample report is for demonstration purposes and should not be used as a final engineering study.",
      left,
      324
    );

    // Data page
    doc.addPage();
    doc.setTextColor(27, 58, 92);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(18);
    doc.text("Required SIRS Components - Summary Table", left, 50);

    const startY = 85;
    const colX = [left, 275, 410, 500];
    const rowHeight = 28;

    doc.setFillColor(27, 58, 92);
    doc.rect(left, startY, 512, rowHeight, "F");
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(10);
    doc.text("Component", colX[0] + 8, startY + 18);
    doc.text("Replacement Cost", colX[1] + 8, startY + 18);
    doc.text("RUL (Years)", colX[2] + 8, startY + 18);
    doc.text("Current Reserves", colX[3] + 8, startY + 18);

    doc.setTextColor(27, 58, 92);
    doc.setFont("helvetica", "normal");

    sampleRows.forEach((row, idx) => {
      const y = startY + rowHeight + idx * rowHeight;
      if (idx % 2 === 0) {
        doc.setFillColor(243, 247, 251);
        doc.rect(left, y, 512, rowHeight, "F");
      }
      doc.rect(left, y, 512, rowHeight);
      doc.text(row[0], colX[0] + 8, y + 18);
      doc.text(row[1], colX[1] + 8, y + 18);
      doc.text(row[2], colX[2] + 8, y + 18);
      doc.text(row[3], colX[3] + 8, y + 18);
    });

    doc.setFontSize(10);
    doc.setTextColor(70, 70, 70);
    doc.text(
      "Engineering note: Replacement costs shown are present-value estimates subject to inflation, market conditions,",
      left,
      390
    );
    doc.text(
      "and scope refinement during final design and procurement.",
      left,
      406
    );

    doc.save("Oceanview-Towers-Sample-SIRS-Report.pdf");
  }

  const chartData = {
    labels: visibleProjection.map((row) => `Y${row.year}`),
    datasets: [
      {
        label: "Projected Ending Balance",
        data: visibleProjection.map((row) => row.endingBalance),
        borderColor: BRAND.navy,
        backgroundColor: "rgba(27,58,92,0.15)",
        borderWidth: 2,
      },
      ...(savedScenario
        ? [
            {
              label: `${savedScenario.label} Ending Balance`,
              data: (isPro ? savedScenario.projection : savedScenario.projection.slice(0, 10)).map((row) => row.endingBalance),
              borderColor: BRAND.teal,
              backgroundColor: "rgba(42,157,143,0.15)",
              borderWidth: 2,
            },
          ]
        : []),
    ],
  };

  const commonCardStyle: React.CSSProperties = {
    border: `1px solid ${BRAND.border}`,
    borderRadius: 10,
    padding: 16,
    backgroundColor: BRAND.bg,
    boxShadow: "0 2px 6px rgba(27,58,92,0.06)",
    marginBottom: 16,
  };

  return (
    <div style={{ background: BRAND.bg, minHeight: "100vh", color: BRAND.navy, fontFamily: "Arial, sans-serif" }}>
      <nav
        style={{
          display: "flex",
          gap: 10,
          alignItems: "center",
          padding: "14px 20px",
          borderBottom: `2px solid ${BRAND.navy}`,
          background: BRAND.light,
        }}
      >
        <Link
          to="/"
          style={{ textDecoration: "none", display: "flex", alignItems: "center", gap: "10px", marginRight: 20 }}
        >
          <img src="/logo.png" alt="FloridaSIRS" style={{ height: "56px", verticalAlign: "middle" }} />
          <h2 style={{ margin: 0, color: BRAND.navy, fontSize: 22, fontWeight: 600 }}>FloridaSIRS.org</h2>
        </Link>
        {[
          { key: "upload", label: "Upload SIRS Report" },
          { key: "funding", label: "Funding Plan" },
          { key: "owner", label: "Owner Report" },
        ].map((tab) => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key as TabKey)}
            style={{
              padding: "8px 12px",
              borderRadius: 6,
              border: `1px solid ${activeTab === tab.key ? BRAND.navy : BRAND.border}`,
              backgroundColor: activeTab === tab.key ? BRAND.navy : "#fff",
              color: activeTab === tab.key ? "#fff" : BRAND.navy,
              cursor: "pointer",
            }}
          >
            {tab.label}
          </button>
        ))}
        <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
          {!session ? (
            <button
              type="button"
              onClick={() => setShowAuthModal(true)}
              style={{
                padding: "8px 14px",
                borderRadius: 6,
                border: `1px solid ${BRAND.navy}`,
                background: "#fff",
                color: BRAND.navy,
                fontWeight: 600,
                fontSize: 13,
                cursor: "pointer",
              }}
            >
              Sign In
            </button>
          ) : (
            <>
              <span
                style={{
                  fontSize: 12,
                  color: BRAND.navy,
                  maxWidth: 200,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
                title={session.user?.email ?? ""}
              >
                {session.user?.email}
              </span>
              <button
                type="button"
                onClick={() => void supabase.auth.signOut()}
                style={{
                  padding: "8px 14px",
                  borderRadius: 6,
                  border: `1px solid ${BRAND.border}`,
                  background: "#fff",
                  color: BRAND.navy,
                  fontWeight: 600,
                  fontSize: 13,
                  cursor: "pointer",
                }}
              >
                Sign Out
              </button>
              {isProPlanOnly && (
                <button
                  type="button"
                  disabled={cancelSubscriptionLoading}
                  onClick={() => void handleCancelSubscription()}
                  style={{
                    padding: 0,
                    border: "none",
                    background: "none",
                    color: "#9a3412",
                    fontWeight: 600,
                    fontSize: 13,
                    cursor: cancelSubscriptionLoading ? "wait" : "pointer",
                    textDecoration: "underline",
                    whiteSpace: "nowrap",
                  }}
                >
                  {cancelSubscriptionLoading ? "Cancelling…" : "Cancel subscription"}
                </button>
              )}
            </>
          )}
          {!isPro && (
            <a
              href={UPGRADE_URL}
              target="_blank"
              rel="noreferrer"
              style={{
                color: BRAND.teal,
                textDecoration: "none",
                fontWeight: 700,
                padding: "0 4px",
                fontSize: 13,
                whiteSpace: "nowrap",
              }}
            >
              Upgrade to Pro →
            </a>
          )}
        </div>
      </nav>

      <main style={{ padding: 20, maxWidth: 1200, margin: "0 auto" }}>
        {activeTab === "upload" && (
          <section>
            <div style={commonCardStyle}>
              <h3>Upload SIRS PDF</h3>
              <button
                onClick={downloadSampleSirsPdf}
                style={{
                  marginBottom: 12,
                  backgroundColor: BRAND.teal,
                  color: "#fff",
                  border: "none",
                  borderRadius: 6,
                  padding: "8px 12px",
                  cursor: "pointer",
                  fontWeight: 600,
                }}
              >
                Download Sample SIRS PDF
              </button>
              <div style={{ marginTop: 12, position: "relative" }}>
                <input
                  id="sirs-pdf-upload"
                  type="file"
                  accept=".pdf"
                  aria-label="Choose PDF file to upload"
                  style={{
                    position: "absolute",
                    width: 1,
                    height: 1,
                    padding: 0,
                    margin: -1,
                    overflow: "hidden",
                    clip: "rect(0,0,0,0)",
                    whiteSpace: "nowrap",
                    border: 0,
                  }}
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) handlePdfUpload(file);
                  }}
                />
                <label
                  htmlFor="sirs-pdf-upload"
                  style={{
                    display: "inline-block",
                    cursor: "pointer",
                    padding: "10px 20px",
                    border: `2px solid ${BRAND.navy}`,
                    color: BRAND.navy,
                    backgroundColor: BRAND.bg,
                    borderRadius: 6,
                    fontWeight: 600,
                    fontSize: 14,
                    letterSpacing: "0.02em",
                  }}
                >
                  Choose File
                </label>
              </div>
              {pdfName && <p style={{ marginBottom: 0 }}>File: {pdfName}</p>}
              {isParsing && <p style={{ color: BRAND.teal }}>Analysing your SIRS report...</p>}
              {uploadError && <p style={{ color: "red" }}>{uploadError}</p>}
            </div>

            <div style={commonCardStyle}>
              <h3>Extracted Components (Editable)</h3>
              <p style={{ color: "red", fontWeight: 600 }}>
                Review all extracted values before generating your funding plan
              </p>
              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead>
                    <tr>
                      {["Component", "Estimated Replacement Cost", "Remaining Useful Life (Years)", "Current Reserve Balance"].map(
                        (head) => (
                          <th
                            key={head}
                            style={{
                              textAlign: "left",
                              borderBottom: `1px solid ${BRAND.border}`,
                              padding: 8,
                              backgroundColor: BRAND.light,
                            }}
                          >
                            {head}
                          </th>
                        )
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {components.map((row, index) => {
                      const isLocked = !isPro && index >= 3;
                      if (isLocked) {
                        return (
                          <tr key={`${row.name}-locked`} style={{ backgroundColor: "#F7F7F7" }}>
                            <td colSpan={4} style={{ borderBottom: `1px solid ${BRAND.border}`, padding: 10 }}>
                              <div
                                style={{
                                  filter: "blur(1.5px)",
                                  opacity: 0.75,
                                  userSelect: "none",
                                  pointerEvents: "none",
                                }}
                              >
                                <strong>🔒 {row.name}</strong> — Upgrade to Pro to unlock all 8 components
                              </div>
                            </td>
                          </tr>
                        );
                      }
                      return (
                        <tr key={row.name}>
                          <td style={{ borderBottom: `1px solid ${BRAND.border}`, padding: 8 }}>
                            <input
                              value={row.name}
                              onChange={(e) => updateComponent(index, "name", e.target.value)}
                              style={{ width: "100%", padding: 7 }}
                            />
                          </td>
                          <td style={{ borderBottom: `1px solid ${BRAND.border}`, padding: 8 }}>
                            <input
                              type="text"
                              inputMode="numeric"
                              value={moneyFieldDisplay(row, index, "erc")}
                              onFocus={() => setFocusedMoney({ index, field: "erc" })}
                              onBlur={() => setFocusedMoney(null)}
                              onChange={(e) => updateComponent(index, "estimatedReplacementCost", e.target.value)}
                              style={{ width: "100%", padding: 7 }}
                            />
                          </td>
                          <td style={{ borderBottom: `1px solid ${BRAND.border}`, padding: 8 }}>
                            <input
                              value={row.remainingUsefulLifeYears}
                              onChange={(e) => updateComponent(index, "remainingUsefulLifeYears", e.target.value)}
                              style={{ width: "100%", padding: 7 }}
                            />
                          </td>
                          <td style={{ borderBottom: `1px solid ${BRAND.border}`, padding: 8 }}>
                            <input
                              type="text"
                              inputMode="numeric"
                              value={moneyFieldDisplay(row, index, "crb")}
                              onFocus={() => setFocusedMoney({ index, field: "crb" })}
                              onBlur={() => setFocusedMoney(null)}
                              onChange={(e) => updateComponent(index, "currentReserveBalance", e.target.value)}
                              style={{ width: "100%", padding: 7 }}
                            />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              {!isPro && (
                <div
                  style={{
                    marginTop: 16,
                    backgroundColor: BRAND.teal,
                    color: "#fff",
                    borderRadius: 10,
                    padding: "14px 16px",
                    border: "1px solid rgba(255,255,255,0.25)",
                  }}
                >
                  <p style={{ margin: "0 0 12px 0", fontWeight: 700, lineHeight: 1.35 }}>
                    Upgrade to Pro to unlock all 8 components, the full 30-year HB 913 baseline funding plan, PDF export,
                    and owner-facing reports. $29/month.
                  </p>
                  <a
                    href={UPGRADE_URL}
                    target="_blank"
                    rel="noreferrer"
                    style={{
                      display: "inline-block",
                      backgroundColor: "#fff",
                      color: BRAND.teal,
                      fontWeight: 800,
                      textDecoration: "none",
                      borderRadius: 8,
                      padding: "10px 14px",
                      border: `1px solid ${BRAND.teal}`,
                    }}
                  >
                    Upgrade to Pro →
                  </a>
                </div>
              )}
            </div>
          </section>
        )}

        {activeTab === "funding" && (
          <section>
            {!isPro && (
              <div
                style={{
                  backgroundColor: BRAND.teal,
                  color: "#fff",
                  borderRadius: 10,
                  padding: "14px 16px",
                  marginBottom: 16,
                  border: "1px solid rgba(255,255,255,0.25)",
                  boxShadow: "0 2px 6px rgba(27,58,92,0.08)",
                  maxWidth: 980,
                  marginLeft: "auto",
                  marginRight: "auto",
                }}
              >
                <div style={{ fontWeight: 800, lineHeight: 1.25, marginBottom: 10 }}>
                  Upgrade to Pro — Full 30-year projections, unlimited scenarios, PDF export and owner reports. $29/month.
                </div>
                <a
                  href={UPGRADE_URL}
                  target="_blank"
                  rel="noreferrer"
                  style={{
                    display: "inline-block",
                    backgroundColor: "#fff",
                    color: BRAND.teal,
                    fontWeight: 900,
                    textDecoration: "none",
                    borderRadius: 8,
                    padding: "10px 14px",
                    border: `1px solid ${BRAND.teal}`,
                  }}
                >
                  Upgrade to Pro →
                </a>
              </div>
            )}
            <div style={commonCardStyle}>
              <h3>Funding Inputs</h3>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(220px,1fr))", gap: 12 }}>
                {[
                  ["totalCurrentReserveBalance", "Total current reserve balance"],
                  ["annualContributionAmount", "Annual contribution amount"],
                  ["inflationRatePct", "Expected annual inflation rate (%)"],
                  ["interestRatePct", "Expected annual interest on reserves (%)"],
                ].map(([key, label]) => (
                  <div key={key}>
                    <label>{label}</label>
                    <input
                      value={fundingInputs[key as keyof FundingInputs]}
                      onChange={(e) => updateFundingField(key as keyof FundingInputs, e.target.value)}
                      style={{ width: "100%", padding: 8, border: `1px solid ${BRAND.border}`, borderRadius: 6 }}
                    />
                  </div>
                ))}
                <div>
                  <label>Number of units in association</label>
                  <input
                    value={numberOfUnits}
                    onChange={(e) => setNumberOfUnits(Math.max(0, parseNumber(e.target.value)))}
                    style={{ width: "100%", padding: 8, border: `1px solid ${BRAND.border}`, borderRadius: 6 }}
                  />
                </div>
              </div>

              {contributionsInsufficient && (
                <p style={{ color: "red", fontWeight: 700, marginTop: 14 }}>
                  Warning: Your current contributions are insufficient. Minimum required contribution:{" "}
                  {asCurrency(minimumContributionResult.minimum / 12)}/month
                </p>
              )}
              <div
                style={{
                  marginTop: 10,
                  backgroundColor: "#EAF8EE",
                  border: "1px solid #9DD5AB",
                  color: "#1F6B36",
                  borderRadius: 8,
                  padding: "10px 12px",
                  fontWeight: 700,
                }}
              >
                Required monthly contribution per unit: {asCurrencyPrecise(minimumMonthlyPerUnit)}
              </div>
              <button
                onClick={() =>
                  setSavedScenario({
                    label: `Scenario (${new Date().toLocaleTimeString()})`,
                    inputs: { ...fundingInputs },
                    minAnnualContribution: minimumContributionResult.minimum,
                    projection: [...projectionData],
                  })
                }
                style={{
                  marginTop: 8,
                  backgroundColor: BRAND.teal,
                  color: "#fff",
                  border: "none",
                  borderRadius: 6,
                  padding: "8px 12px",
                  cursor: "pointer",
                }}
              >
                Save current scenario for comparison
              </button>
            </div>

            <div style={commonCardStyle}>
              <h3>{isPro ? "30-Year" : "10-Year (preview)"} Balance Projection</h3>
              <Line
                key={isPro ? "pro-chart" : "free-chart"}
                data={chartData}
                options={{
                  responsive: true,
                  plugins: { legend: { position: "top" as const }, title: { display: false, text: "Balances" } },
                }}
              />
            </div>

            {savedScenario && (
              <div style={commonCardStyle}>
                <h3>Scenario Comparison</h3>
                <p>
                  Saved annual contribution: {asCurrency(savedScenario.inputs.annualContributionAmount)} | Current annual
                  contribution: {asCurrency(fundingInputs.annualContributionAmount)}
                </p>
                <p>
                  Saved ending (Year {isPro ? 30 : 10}): {asCurrency(savedScenario.projection[(isPro ? 30 : 10) - 1]?.endingBalance || 0)} | Current ending
                  (Year {isPro ? 30 : 10}): {asCurrency(projectionData[(isPro ? 30 : 10) - 1]?.endingBalance || 0)}
                </p>
              </div>
            )}

            <div style={commonCardStyle}>
              <div style={{ overflowX: "auto", maxHeight: 420 }}>
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead>
                    <tr>
                      {["Year", "Starting Balance", "Contributions", "Interest Earned", "Component Replacements Due", "Ending Balance"].map(
                        (head) => (
                          <th
                            key={head}
                            style={{ textAlign: "left", padding: 8, borderBottom: `1px solid ${BRAND.border}`, background: BRAND.light }}
                          >
                            {head}
                          </th>
                        )
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {visibleProjection.map((row) => (
                      <tr key={row.year} style={{ backgroundColor: row.replacementsDue > 0 ? "#FFF8CC" : "#FFFFFF" }}>
                        <td style={{ padding: 8, borderBottom: `1px solid ${BRAND.border}` }}>{row.year}</td>
                        <td style={{ padding: 8, borderBottom: `1px solid ${BRAND.border}` }}>{asCurrency(row.startingBalance)}</td>
                        <td style={{ padding: 8, borderBottom: `1px solid ${BRAND.border}` }}>{asCurrency(row.contributions)}</td>
                        <td style={{ padding: 8, borderBottom: `1px solid ${BRAND.border}` }}>{asCurrency(row.interestEarned)}</td>
                        <td style={{ padding: 8, borderBottom: `1px solid ${BRAND.border}` }}>{asCurrency(row.replacementsDue)}</td>
                        <td
                          style={{
                            padding: 8,
                            borderBottom: `1px solid ${BRAND.border}`,
                            color: row.endingBalance < 0 ? "red" : BRAND.navy,
                            fontWeight: 600,
                          }}
                        >
                          {asCurrency(Math.abs(row.endingBalance) < 0.5 ? 0 : row.endingBalance)}
                        </td>
                      </tr>
                    ))}
                    {!isPro && (
                      <tr>
                        <td
                          colSpan={6}
                          style={{
                            padding: 14,
                            backgroundColor: BRAND.teal,
                            color: "#fff",
                            fontWeight: 700,
                            textAlign: "center",
                            borderBottom: `1px solid ${BRAND.border}`,
                          }}
                        >
                          <span style={{ marginRight: 12 }}>
                            🔒 Years 11-30 locked — Upgrade to Pro for the full HB 913 baseline funding plan
                          </span>
                          <a
                            href={UPGRADE_URL}
                            target="_blank"
                            rel="noreferrer"
                            style={{
                              display: "inline-block",
                              backgroundColor: "#fff",
                              color: BRAND.teal,
                              fontWeight: 800,
                              textDecoration: "none",
                              borderRadius: 8,
                              padding: "8px 12px",
                            }}
                          >
                            Upgrade to Pro →
                          </a>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </section>
        )}

        {activeTab === "owner" && (
          <section>
            <div style={{ ...commonCardStyle, maxWidth: 900, margin: "0 auto 16px auto", position: "relative" }}>
              <div style={isPro ? undefined : { filter: "blur(4px)", pointerEvents: "none", userSelect: "none" }}>
                <h3 style={{ marginBottom: 8 }}>Owner Report (Printable One-Page Summary)</h3>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 14 }}>
                  <div>
                    <label>Association name</label>
                    <input
                      value={associationName}
                      onChange={(e) => setAssociationName(e.target.value)}
                      style={{ width: "100%", padding: 8, border: `1px solid ${BRAND.border}`, borderRadius: 6 }}
                    />
                  </div>
                  <div>
                    <label>Number of units</label>
                    <input
                      value={numberOfUnits}
                      onChange={(e) => setNumberOfUnits(Math.max(0, parseNumber(e.target.value)))}
                      style={{ width: "100%", padding: 8, border: `1px solid ${BRAND.border}`, borderRadius: 6 }}
                    />
                  </div>
                </div>

                <p><strong>Association:</strong> {associationName}</p>
                <p><strong>Report date:</strong> {new Date().toLocaleDateString()}</p>
                <p>
                  <strong>Monthly assessment required per unit:</strong> {asCurrency(monthlyRequiredPerUnit)}
                </p>

                <h4>Top 3 Upcoming Replacements</h4>
                {topThreeUpcoming.length === 0 && <p>No replacement schedule available yet.</p>}
                {topThreeUpcoming.map((item) => (
                  <p key={item.name}>
                    {item.name}: {asCurrency(item.futureCost)} in approximately {item.years} years
                  </p>
                ))}

                <h4>30-Year Funding Chart</h4>
                <Line
                  ref={ownerChartRef}
                  data={{
                    labels: (isPro ? projectionData : projectionData.slice(0, 10)).map((row) => row.year.toString()),
                    datasets: [
                      {
                        label: "Ending Balance",
                        data: (isPro ? projectionData : projectionData.slice(0, 10)).map((row) => row.endingBalance),
                        borderColor: BRAND.navy,
                        backgroundColor: "rgba(27,58,92,0.15)",
                        fill: true,
                      },
                    ],
                  }}
                  options={{ responsive: true, plugins: { legend: { display: true } } }}
                />

                <h4>Plain-English Explanation</h4>
                <p>
                  This report estimates long-term reserve funding for major building components over the next 30 years.
                  Based on your assumptions for inflation, interest, and scheduled replacements, it calculates the funding
                  needed each year so reserves do not fall below zero and shows the monthly amount required per unit.
                </p>

                <div style={{ display: "flex", gap: 10 }}>
                  {isPro && (
                    <button
                      onClick={downloadOwnerPdf}
                      style={{ background: BRAND.navy, color: "#fff", border: "none", padding: "8px 12px", borderRadius: 6, cursor: "pointer" }}
                    >
                      Download PDF
                    </button>
                  )}
                  <button
                    onClick={copyShareableLink}
                    style={{ background: BRAND.teal, color: "#fff", border: "none", padding: "8px 12px", borderRadius: 6, cursor: "pointer" }}
                  >
                    Copy shareable link
                  </button>
                </div>
              </div>
              {!isPro && (
                <div
                  style={{
                    position: "absolute",
                    inset: 0,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    textAlign: "center",
                    background: "rgba(255,255,255,0.56)",
                    padding: 24,
                  }}
                >
                  <p style={{ fontWeight: 700, color: BRAND.navy, maxWidth: 620 }}>
                    The Owner Report is a Pro feature. Generate a shareable PDF funding summary for your unit owners.
                  </p>
                  <a
                    href={UPGRADE_URL}
                    target="_blank"
                    rel="noreferrer"
                    style={{
                      marginTop: 8,
                      backgroundColor: BRAND.teal,
                      color: "#fff",
                      textDecoration: "none",
                      borderRadius: 8,
                      padding: "10px 14px",
                      fontWeight: 800,
                    }}
                  >
                    Upgrade to Pro →
                  </a>
                </div>
              )}
            </div>
          </section>
        )}
      </main>

      <footer style={{ padding: "12px 20px", borderTop: `1px solid ${BRAND.border}`, color: "#666", textAlign: "center" }}>
        <small>
          © 2026 FloridaSIRS.org — Florida HB 913 Compliance Tool | Not affiliated with DBPR | Not legal or financial advice
        </small>
      </footer>

      <AuthModal open={showAuthModal} onClose={() => setShowAuthModal(false)} />
    </div>
  );
}

export default MainApp;
